import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PersonPicker } from '../components/pickers';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Field, Input, PageHeader, PageLoader, Select, Table, Td, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { formatUtcDate, personName } from '../lib/format';
import { invalidateInventory, useRooms } from '../lib/hooks';
import { isOpenOn, localToday } from '../lib/locations';
import type { PersonSummary, RolloverRoom, RolloverSummary, RoomAssignmentsOnDate } from '../lib/types';

const KEEP = '__keep';
const NONE = '__none';

interface Row {
  person: PersonSummary;
  current: { id: string; name: string; building: { name: string } }[];
  /** KEEP, NONE, or a room id. */
  next: string;
}

const roomList = (rooms: RolloverRoom[]) => rooms.map((room) => `${room.name} (${room.building})`).join(', ');

export function RoomRolloverPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const today = localToday();
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [rows, setRows] = useState<Row[]>([]);
  const [preview, setPreview] = useState<RolloverSummary | null>(null);
  const [adding, setAdding] = useState(false);
  const rooms = useRooms();
  const assignments = useQuery({
    queryKey: ['room-assignments', today],
    queryFn: () => api.get<RoomAssignmentsOnDate>('/rooms/assignments', { on: today }),
  });

  useEffect(() => {
    if (assignments.data) setRows(assignments.data.people.map((entry) => ({ person: entry.person, current: entry.rooms, next: KEEP })));
  }, [assignments.data]);

  const openRooms = useMemo(
    () => (rooms.data ?? []).filter((room) => isOpenOn(room, effectiveDate) && isOpenOn(room.building, effectiveDate)),
    [rooms.data, effectiveDate],
  );
  const byBuilding = useMemo(() => {
    const groups = new Map<string, typeof openRooms>();
    for (const room of openRooms) groups.set(room.building?.name ?? 'Other', [...(groups.get(room.building?.name ?? 'Other') ?? []), room]);
    return [...groups.entries()];
  }, [openRooms]);

  const changes = rows.filter((row) => row.next !== KEEP).map((row) => ({ personId: row.person.id, roomIds: row.next === NONE ? [] : [row.next] }));
  const edit = (update: (rows: Row[]) => Row[]) => {
    setRows(update);
    setPreview(null);
  };

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<RolloverSummary>('/rooms/rollover', { effectiveDate, changes, dryRun }),
    onSuccess: (summary) => {
      if (!summary.applied) {
        setPreview(summary);
        return;
      }
      invalidateInventory(queryClient);
      setPreview(null);
      toast.success(`Saved. ${summary.totals.starting} room assignments start ${formatUtcDate(effectiveDate)}, and ${summary.totals.ending} end the day before.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (assignments.isLoading) return <PageLoader />;
  if (assignments.error) return <ErrorState error={assignments.error} onRetry={() => assignments.refetch()} />;

  return (
    <>
      <PageHeader
        back={{ to: '/locations', label: 'Locations' }}
        title="Year-start room rollover"
        description="Move teachers and staff to their rooms for the new year in one step. Rooms they leave end the day before, so there’s nothing to close out by hand."
      />

      <div className="space-y-6">
        <Card>
          <div className="flex flex-wrap items-end gap-6 p-5">
            <Field label="New rooms start on">
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => {
                  setEffectiveDate(e.target.value || today);
                  setPreview(null);
                }}
                className="w-44"
              />
            </Field>
            <p className="max-w-xl text-sm text-slate-500">
              Until then, everyone stays responsible for the rooms they have now. Reports and investigations keep seeing who had each room on
              every day.
            </p>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="People and rooms"
            description={`${rows.length} ${rows.length === 1 ? 'person' : 'people'} · ${changes.length} to change`}
            actions={
              <Button size="sm" icon={<UserPlus />} onClick={() => setAdding((value) => !value)}>
                Add someone
              </Button>
            }
          />
          {adding && (
            <div className="border-b border-slate-100 p-4">
              <PersonPicker
                inline
                value={null}
                placeholder="New staff member, or someone without a room yet"
                onChange={(person) => {
                  if (!person) return;
                  if (!rows.some((row) => row.person.id === person.id)) edit((current) => [...current, { person, current: [], next: NONE }]);
                  setAdding(false);
                }}
              />
            </div>
          )}
          {rows.length === 0 ? (
            <EmptyState title="No one is responsible for a room yet" description="Add people, then choose their rooms." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Person</Th>
                  <Th>Now</Th>
                  <Th>From {formatUtcDate(effectiveDate)}</Th>
                  <Th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.person.id} className={row.next !== KEEP ? 'bg-brand-50/40' : undefined}>
                    <Td>
                      <p className="font-medium text-slate-900">{personName(row.person)}</p>
                      {row.person.email && <p className="text-xs text-slate-500">{row.person.email}</p>}
                    </Td>
                    <Td className="text-slate-600">
                      {row.current.length ? row.current.map((room) => `${room.name} (${room.building.name})`).join(', ') : <span className="text-slate-400">No room</span>}
                    </Td>
                    <Td className="w-72">
                      <Select value={row.next} onChange={(e) => edit((current) => current.map((item) => (item.person.id === row.person.id ? { ...item, next: e.target.value } : item)))}>
                        <option value={KEEP}>{row.current.length ? 'Keep current rooms' : 'Leave without a room'}</option>
                        {row.current.length > 0 && <option value={NONE}>No room</option>}
                        {byBuilding.map(([building, options]) => (
                          <optgroup key={building} label={building}>
                            {options.map((room) => (
                              <option key={room.id} value={room.id}>
                                {room.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => edit((current) => current.filter((item) => item.person.id !== row.person.id))}
                        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label={`Leave ${personName(row.person)} out of the rollover`}
                      >
                        <X className="size-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 p-4">
            <p className="text-sm text-slate-500">People with more than one room can keep them all, or be given one room here and more on the room pages.</p>
            <Button variant="primary" icon={<ArrowRight />} disabled={changes.length === 0} loading={run.isPending && !preview} onClick={() => run.mutate(true)}>
              Preview {changes.length || ''} change{changes.length === 1 ? '' : 's'}
            </Button>
          </div>
        </Card>

        {preview && (
          <Card>
            <CardHeader
              title={`What changes on ${formatUtcDate(preview.effectiveDate)}`}
              description="Nothing is saved until you apply it"
              actions={
                <Button variant="primary" loading={run.isPending} onClick={() => run.mutate(false)}>
                  Apply rollover
                </Button>
              }
            />
            <div className="flex flex-wrap gap-2 border-b border-slate-100 px-5 py-3">
              <Badge tone="green">{preview.totals.starting} starting</Badge>
              <Badge tone="amber">{preview.totals.ending} ending</Badge>
              <Badge>{preview.totals.keeping} kept</Badge>
            </div>
            {preview.totals.starting === 0 && preview.totals.ending === 0 ? (
              <Alert tone="blue" className="m-5">
                Everyone already has those rooms.
              </Alert>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Person</Th>
                    <Th>Ends</Th>
                    <Th>Starts</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.people
                    .filter((entry) => entry.starting.length || entry.ending.length)
                    .map((entry) => (
                      <tr key={entry.person.id}>
                        <Td className="font-medium text-slate-900">{personName(entry.person)}</Td>
                        <Td className="text-slate-600">{roomList(entry.ending) || '—'}</Td>
                        <Td className="text-slate-900">{roomList(entry.starting) || '—'}</Td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
